import { motion } from "framer-motion";
import { HeartHandshake, Globe2 } from "lucide-react";
import TierBadge from "../TierBadge";

/**
 * Iter-27.65 — Hero immersif premium remanié pour clarté :
 *  - Fallback visuel décoratif (pattern hexagonal subtil) quand pas d'image
 *  - Texte tier badge + "ESPACE PARTENAIRE" beaucoup plus lisibles (gradient
 *    bottom plus marqué, white solide pour la sur-ligne)
 *  - Mini-intro explicite "Découvre les remerciements et les histoires d'impact"
 *  - Compteurs Remerciements / Histoires d'impact en bas du hero (donne la
 *    promesse au premier coup d'œil sans devoir scroller)
 */
export default function PartnerHero({ data, gratitudeCount = 0, impactCount = 0 }) {
    const hero = data.hero_image_url || data.cover_image;
    const accent = data.primary_color || "#EE722E";

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl overflow-hidden mb-5"
            data-testid="partner-hero"
        >
            {/* Background */}
            <div className="relative h-56 sm:h-64 lg:h-72">
                {hero ? (
                    <img
                        src={hero}
                        alt={data.name}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <>
                        {/* Fallback décoratif : gradient + pattern radial pour ajouter de la texture */}
                        <div
                            className="absolute inset-0"
                            style={{
                                background: `linear-gradient(135deg, ${accent} 0%, ${accent}99 45%, #181818 100%)`,
                            }}
                        />
                        <div
                            className="absolute inset-0 opacity-25"
                            style={{
                                backgroundImage: `radial-gradient(circle at 25% 30%, rgba(255,255,255,0.18) 0, transparent 35%), radial-gradient(circle at 80% 75%, rgba(0,0,0,0.25) 0, transparent 40%)`,
                            }}
                        />
                    </>
                )}
                {/* Gradient overlay marqué bottom pour lisibilité texte (plus opaque qu'avant) */}
                <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-shariti-black via-shariti-black/85 to-transparent" />
                <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-shariti-black/40 to-transparent" />
            </div>

            {/* Eyebrow top-left "ESPACE PARTENAIRE" — toujours visible sur n'importe quel fond */}
            <div className="absolute top-4 left-5 sm:top-5 sm:left-7 z-10">
                <span
                    className="inline-block text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.32em] text-white px-2.5 py-1 rounded-full"
                    style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}
                >
                    Espace partenaire
                </span>
            </div>

            {/* Tier badge top-right */}
            {data.tier && (
                <div className="absolute top-4 right-5 sm:top-5 sm:right-7 z-10">
                    <TierBadge tier={data.tier} size="md" />
                </div>
            )}

            {/* Bloc bas : logo + nom + tagline + promesse */}
            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 z-10">
                <div className="flex items-end gap-4 flex-wrap">
                    {data.logo_url && (
                        <div className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white p-1.5 flex items-center justify-center shadow-2xl">
                            <img
                                src={data.logo_url}
                                alt={data.name}
                                className="w-full h-full object-contain"
                            />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight drop-shadow-lg">
                            {data.name}
                        </h1>
                        {data.tagline && (
                            <p className="text-sm sm:text-base text-white/90 font-light mt-1.5 max-w-2xl drop-shadow">
                                {data.tagline}
                            </p>
                        )}
                    </div>
                </div>

                {/* Promise chips — count des contenus accessibles */}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <PromiseChip
                        icon={HeartHandshake}
                        label="Remerciements"
                        count={gratitudeCount}
                        color={accent}
                    />
                    <PromiseChip
                        icon={Globe2}
                        label="Histoires d'impact"
                        count={impactCount}
                        color={accent}
                    />
                </div>
            </div>
        </motion.div>
    );
}

function PromiseChip({ icon: Icon, label, count, color }) {
    return (
        <span
            className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full"
            style={{
                background: "rgba(0,0,0,0.4)",
                backdropFilter: "blur(8px)",
                color: "#fff",
                border: `1px solid ${color}55`,
            }}
        >
            <Icon className="w-3.5 h-3.5" style={{ color }} />
            <span>{label}</span>
            <span
                className="ml-0.5 px-1.5 rounded-full text-[10px] font-extrabold"
                style={{ background: color, color: "#181818" }}
            >
                {count}
            </span>
        </span>
    );
}
